import type { TaskStatus, TaskSummary } from '@todo/shared';

/**
 * Shared look-and-feel for task cards - one file decides the tones, the due
 * label, and the urgency buckets so every page renders tasks identically.
 */

/** Priority dot (compact, e.g. next to a status pill). */
export const PRIORITY_DOT: Record<string, string> = {
  HIGH: 'bg-red-500',
  MEDIUM: 'bg-amber-400',
  LOW: 'bg-slate-300',
};

/** Priority text badge, used wherever there is room to spell it out. */
export const PRIORITY_BADGE: Record<string, string> = {
  HIGH: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  MEDIUM: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  LOW: 'bg-slate-100 text-slate-500 ring-1 ring-slate-200',
};

/** The card's left edge + status pill share one tone per status. */
export const STATUS_TONE: Record<TaskStatus, { pill: string; dot: string; edge: string }> = {
  DRAFT: { pill: 'bg-slate-50 text-slate-500 ring-slate-200', dot: 'bg-slate-300', edge: 'border-l-slate-300' },
  PENDING: { pill: 'bg-slate-100 text-slate-600 ring-slate-200', dot: 'bg-slate-400', edge: 'border-l-slate-400' },
  ONGOING: { pill: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500', edge: 'border-l-amber-500' },
  BLOCKED: { pill: 'bg-red-50 text-red-700 ring-red-200', dot: 'bg-red-500', edge: 'border-l-red-500' },
  DONE: { pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-600', edge: 'border-l-emerald-600' },
  CANCELLED: { pill: 'bg-slate-50 text-slate-400 ring-slate-200', dot: 'bg-slate-300', edge: 'border-l-slate-300' },
};

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '').join('');
}

export type DueBucket = 'overdue' | 'today' | 'upcoming' | 'none';

export type DueInfo = { text: string; bucket: DueBucket; cls: string };

/** One place decides both the label and which urgency group the card lands in. */
export function dueInfo(task: TaskSummary): DueInfo {
  const closed = task.status === 'DONE' || task.status === 'CANCELLED';

  if (!task.dueAt) {
    return { text: 'No due date', bucket: 'none', cls: 'text-slate-400' };
  }

  const due = new Date(task.dueAt);
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const days = Math.floor((due.getTime() - startOfToday.getTime()) / 86_400_000);

  if (days < 0) {
    return closed
      ? { text: due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }), bucket: 'overdue', cls: 'text-slate-400' }
      : { text: `${Math.abs(days)}d overdue`, bucket: 'overdue', cls: 'font-semibold text-red-600' };
  }
  if (days === 0) return { text: 'Due today', bucket: 'today', cls: closed ? 'text-slate-400' : 'text-amber-700' };
  if (days === 1) return { text: 'Due tomorrow', bucket: 'upcoming', cls: 'text-slate-500' };
  if (days <= 7) return { text: `Due in ${days}d`, bucket: 'upcoming', cls: 'text-slate-500' };

  return {
    text: due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
    bucket: 'upcoming',
    cls: 'text-slate-500',
  };
}

export const DUE_GROUPS: { key: DueBucket; label: string; dot: string }[] = [
  { key: 'overdue', label: 'Overdue', dot: 'bg-red-500' },
  { key: 'today', label: 'Due today', dot: 'bg-amber-500' },
  { key: 'upcoming', label: 'Upcoming', dot: 'bg-sky-500' },
  { key: 'none', label: 'No due date', dot: 'bg-slate-300' },
];

/** Bucket a list by urgency, dropping empty groups. */
export function groupByDue(tasks: TaskSummary[]) {
  return DUE_GROUPS
    .map((g) => ({ ...g, tasks: tasks.filter((t) => dueInfo(t).bucket === g.key) }))
    .filter((g) => g.tasks.length > 0);
}
